// Global
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import {
  Placeholder,
  useSitecoreContext,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { CookieValueTypes, getCookie } from 'cookies-next';
// Components
import React from 'react';
import Component from 'src/helpers/Component/Component';
import Headline from 'src/helpers/Headline/Headline';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import LinkWrapper from 'src/helpers/LinkWrapper/LinkWrapper';
import ImageWrapper from 'src/helpers/Media/ImageWrapper';
import { useOfferCards } from 'lib/utils/useOfferAPI';
import { useAffiliate } from 'lib/context/AffiliateContext';
import OfferDetailsModal from 'src/helpers/Modals/OfferDetailsModal';
import { useOfferDetailsModalStore } from 'src/helpers/Modals/OfferDetailsModal.store';

export type StickyHeaderFormProps =
  Feature.EnterpriseWeb.RenewalByAndersen.Components.Forms.StickyHeaderForm.StickyHeaderForm;
const StickyHeaderForm = (props: StickyHeaderFormProps) => {
  const { rendering, fields } = props;
  const { isLoading, offerData } = useOfferCards();
  const { callTrackingNumber } = useAffiliate();

  const [isFormSticky, setIsFormSticky] = useState(false);
  const { isModalOpen, setIsModalOpen, setStickyHeaderPresent } = useOfferDetailsModalStore();

  const [endDateString, setEndDateString] = useState('');

  const { sitecoreContext } = useSitecoreContext();
  const [isCookieAvailable, setIsCookieAvailable] = useState<CookieValueTypes>(false);

  const visibileCookieName = fields?.visibilityCookie?.fields?.cookieName?.value;
  const displayOfferChecked = fields?.displayOffer?.value;
  const isDisabledFromPage = sitecoreContext.route?.fields?.hideStickyHeaderForm?.value;

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsFormSticky(true);
      } else {
        setIsFormSticky(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (displayOfferChecked === true) {
      setStickyHeaderPresent(true);
    }
  }, [displayOfferChecked, setStickyHeaderPresent]);

  useEffect(() => {
    setIsCookieAvailable(getCookie(visibileCookieName));
    // we can ignore this as 'visibileCookieName' coming from layout data this will not change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof offerData?.offerEndDate?.value === 'string') {
      setEndDateString(offerData?.offerEndDate?.value);
    } else {
      setEndDateString(offerData?.offerEndDate);
    }
  }, [offerData]);

  if (!fields || isDisabledFromPage || isCookieAvailable) {
    return <></>;
  }

  const RenderUtilityNav = () => {
    const year = endDateString?.substring(0, 4);
    const month = endDateString?.substring(4, 6);
    const day = endDateString?.substring(6, 8);

    const formattedEndDate = `${month}/${day}/${year}`;

    const handleCloseModal = () => {
      setIsModalOpen(!isModalOpen);
    };

    return (
      <>
        {displayOfferChecked && (
          <div className="col-span-12 mx-[calc(50%-50vw)] !h-[47px] bg-black py-xxs">
            <div className="mx-auto flex h-full items-center justify-center ml:max-w-screen-lg">
              {!isLoading && (
                <>
                  <Headline
                    classes="text-sm-xs text-white !font-serif font-bold"
                    fields={{
                      headlineText: {
                        value: offerData?.headlineText?.value || offerData?.data?.headlineText,
                      },
                    }}
                  />
                  <button
                    onClick={handleCloseModal}
                    className="px-s text-body font-bold text-primary"
                  >
                    {offerData?.data?.ctaText || offerData?.link?.ctaText || 'See Offer Details'}
                  </button>
                  {callTrackingNumber && (
                    <LinkWrapper
                      className="border-l border-dark-gray pl-s text-button font-bold text-primary"
                      field={{
                        href: `tel: ${callTrackingNumber || ''}`,
                        text: `Call today ${callTrackingNumber || ''}`,
                      }}
                    />
                  )}
                  <OfferDetailsModal
                    offerData={offerData}
                    formattedEndDate={formattedEndDate}
                    visible={displayOfferChecked}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <Component
      variant="full"
      dataComponent="forms/StickyHeaderForm"
      sectionWrapperClasses={classNames(
        isFormSticky ? 'top-0' : '-top-full',
        'mml:block hidden transition-all ease-in-out duration-700 fixed left-0 right-0 z-[998]',
        getCookie(visibileCookieName) === true && 'hidden' // hide sticky banner when cookie is present
      )}
      id="stickyForm"
      {...props}
    >
      {RenderUtilityNav()}
      <div className="col-span-12 mx-[calc(50%-50vw)] -mt-s flex min-h-[94px] items-center border-solid border-b-gray bg-light-gray shadow-md ml:border-b">
        <div className="mx-auto w-full max-w-screen-lg">
          <div className="flex py-xs ml:w-full ml:flex-row ml:justify-between">
            {/* RBA Logo */}
            <div className="mt-xxs mml:min-w-[150px] lg:min-w-[180px]">
              <LinkWrapper field={fields.logoLink} suppressLinkText>
                <ImageWrapper
                  image={fields.logo}
                  additionalDesktopClasses="!aspect-auto max-w-[200px]"
                />
              </LinkWrapper>
            </div>

            {/* Form Fields */}
            <div className="flex-grow [&_.pages_.grid]:!gap-x-xxxs">
              <Placeholder name="form" rendering={rendering} isHorizontalForm={true} />
            </div>
          </div>
        </div>
      </div>
    </Component>
  );
};

export default withDatasourceCheck()<StickyHeaderFormProps>(StickyHeaderForm);
